<script setup lang="ts">
import type { SentenceType } from 'types/types'
import { computed, onMounted, ref, watch } from 'vue'

import PracticeListenTextView from '@/components/PracticeListenTextView.vue'
import { useKippahReminder } from '@/composables/useKippahReminder'
import { loadPracticeSettings, savePracticeSettings } from '@/composables/usePracticeSettings'
import { usePracticeTracking } from '@/composables/usePracticeTracking'
import { type PracticeListItem, useTextCategories } from '@/composables/useTextCategories'
import store from '@/store'

useKippahReminder()

// --- state ---
type Phase = 'categories' | 'subcards' | 'text'
const phase = ref<Phase>('categories')
const selectedCategory = ref<string | null>(null)
const selectedItem = ref<PracticeListItem | null>(null)
usePracticeTracking(selectedItem)
const sentences = ref<SentenceType[]>([])

// --- text categories from barMitzvah ---
const barMitzvah = computed(() => store.getters.student.barMitzvah)
const { formattedAliyot, formattedHaftarot, sentenceGroupCategoryGroups } = useTextCategories(barMitzvah)

// --- categories to show (only those with items) ---
type CategoryCard = { items: PracticeListItem[]; key: string; label: string }
const categories = computed(() => {
  const cats: CategoryCard[] = []
  if (formattedAliyot.value.length) {
    cats.push({ items: formattedAliyot.value, key: 'aliyot', label: 'Torah Portion' })
  }
  if (formattedHaftarot.value.length) {
    cats.push({ items: formattedHaftarot.value, key: 'haftarot', label: 'Haftarah' })
  }
  for (const group of sentenceGroupCategoryGroups.value) {
    cats.push({ items: group.items, key: `sg_${group.description}`, label: group.description })
  }
  return cats
})

// --- subcards: items within selected category ---
const subcardItems = computed(() => {
  if (!selectedCategory.value) {
    return []
  }
  const cat = categories.value.find((c) => c.key === selectedCategory.value)
  return cat?.items ?? []
})

// --- sentence fetching from store (same pattern as LegacyPracticeSelect) ---
const aliyahSentences = computed<SentenceType[]>(() => {
  const aliyah = selectedItem.value?.aliyah
  if (!aliyah) {
    return []
  }
  return store.getters.student.getAliyahSentences(aliyah.id)
})

const haftarahSentences = computed<SentenceType[]>(() => {
  const haftarah = selectedItem.value?.haftarah
  if (!haftarah) {
    return []
  }
  return store.getters.student.getHaftarahSentences(haftarah.reading.id)
})

const sentenceGroupSentences = computed<SentenceType[]>(() => {
  const sg = selectedItem.value?.sentenceGroup
  if (!sg) {
    return []
  }
  return store.getters.student.getSentenceGroupSentences(sg.reading.id)
})

// update sentences when store data arrives (only one category is active at a time)
watch([aliyahSentences, haftarahSentences, sentenceGroupSentences], ([a, h, sg]) => {
  const v = a.length ? a : h.length ? h : sg
  if (v.length) {
    sentences.value = v
  }
})

// --- navigation ---
function selectCategory(key: string, items: PracticeListItem[]) {
  selectedCategory.value = key
  savePracticeSettings({ category: key })
  if (items.length === 1) {
    // single item — skip subcards, go straight to text
    selectItem(items[0])
  } else {
    phase.value = 'subcards'
  }
}

function selectItem(item: PracticeListItem) {
  selectedItem.value = item
  sentences.value = []
  savePracticeSettings({ itemTitle: item.title })
  // dispatch fetch for the selected item's sentences
  if (item.aliyah) {
    store.dispatch.student.fetchAliyahSentences({ aliyah: item.aliyah })
  } else if (item.haftarah) {
    store.dispatch.student.fetchHaftarahSentences({ haftarah: item.haftarah })
  } else if (item.sentenceGroup) {
    store.dispatch.student.fetchSentenceGroupSentences({ sentenceGroup: item.sentenceGroup })
  }
  phase.value = 'text'
}

function goBack() {
  if (phase.value === 'text') {
    // if the category had only one item, go back to categories not subcards
    if (subcardItems.value.length <= 1) {
      phase.value = 'categories'
      selectedCategory.value = null
    } else {
      phase.value = 'subcards'
    }
    selectedItem.value = null
    sentences.value = []
  } else if (phase.value === 'subcards') {
    phase.value = 'categories'
    selectedCategory.value = null
  }
  savePracticeSettings({ category: null, itemTitle: null })
}

// --- restore saved navigation when categories become available ---
const restored = ref(false)
watch(
  categories,
  (cats) => {
    if (restored.value || !cats.length) {
      return
    }
    restored.value = true
    const saved = loadPracticeSettings()
    if (!saved.category || !saved.itemTitle) {
      return
    }
    const cat = cats.find((c) => c.key === saved.category)
    if (!cat) {
      return
    }
    const item = cat.items.find((i) => i.title === saved.itemTitle)
    if (item) {
      selectedCategory.value = saved.category ?? null
      selectItem(item)
    }
  },
  { immediate: true },
)

// --- lifecycle ---
onMounted(() => {
  store.dispatch.user.fetchUser()
  store.dispatch.student.fetchBarMitzvah()
})
</script>

<template>
  <div>
    <!-- Back button -->
    <v-btn v-if="phase !== 'categories'" class="mb-4" variant="text" @click="goBack">
      <v-icon start>mdi-arrow-left</v-icon>
      Back
    </v-btn>

    <!-- Categories phase -->
    <v-row v-if="phase === 'categories'">
      <v-col v-for="cat in categories" :key="cat.key" cols="12" md="4">
        <v-card class="pa-6 text-center" hover @click="selectCategory(cat.key, cat.items)">
          <v-card-title class="text-h5">{{ cat.label }}</v-card-title>
          <v-card-subtitle>{{ cat.items.length }} {{ cat.items.length === 1 ? 'text' : 'texts' }}</v-card-subtitle>
        </v-card>
      </v-col>
    </v-row>

    <!-- Subcards phase -->
    <v-row v-if="phase === 'subcards'">
      <v-col v-for="item in subcardItems" :key="item.title" cols="12" md="4" sm="6">
        <v-card class="pa-6 text-center" hover @click="selectItem(item)">
          <v-card-title class="text-h5">{{ item.title }}</v-card-title>
        </v-card>
      </v-col>
    </v-row>

    <!-- Text phase -->
    <practice-listen-text-view
      v-if="phase === 'text' && selectedItem"
      :aliyah="selectedItem.aliyah"
      :haftarah="selectedItem.haftarah"
      :sentence-group="selectedItem.sentenceGroup"
      :sentences="sentences"
    />
  </div>
</template>
